import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserPlus, UserCheck, Search, Users } from 'lucide-react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { FloorTheme, getTheme } from '@/lib/themes';
import { Avatar } from '@/components/Avatar';

interface User {
    user_id: number;
    username: string;
    role_type?: string;
    floor_level: number;
    smile_score: number;
    room_no?: string;
    display_name?: string;
    avatar_path?: string;
}

interface FriendRequest {
    request_id: number;
    requester_id: number;
    requester_name?: string;
    requester_username?: string;
    requester_display_name?: string;
    requester_avatar_path?: string;
    requester_floor_level?: number;
    requester_role_type?: string;
    requester_room_no?: string;
    created_at: string;
}

export default function Neighbors() {
    const { theme } = useOutletContext<{ theme: FloorTheme }>();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'my_neighbors' | 'greetings'>('my_neighbors');
    const [friends, setFriends] = useState<User[]>([]);
    const [requests, setRequests] = useState<FriendRequest[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [friendIds, setFriendIds] = useState<Set<number>>(new Set());
    const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());

    const resolveDisplayName = (displayName?: string, roomNo?: string) => {
        if (displayName && displayName.trim()) return displayName;
        if (roomNo && roomNo.trim()) return roomNo;
        return '住人';
    };

    const handleProfileNavigate = (userId?: number) => {
        if (!userId) return;
        navigate(`/users/${userId}`);
    };

    useEffect(() => {
        loadData();
    }, [activeTab]);

    const loadFriends = async () => {
        const res = await api.get('/friends/');
        const list = res.data || [];
        setFriends(list);
        setFriendIds(new Set(list.map((item: User) => item.user_id)));
    };

    const loadData = async () => {
        setLoading(true);
        try {
            await loadFriends();
            if (activeTab === 'greetings') {
                const res = await api.get('/friends/requests');
                setRequests(res.data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        setLoading(true);
        try {
            const query = searchQuery.trim().toUpperCase();
            const res = await api.get(`/users/search/${encodeURIComponent(query)}`);
            setSearchResults(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const sendRequest = async (userId: number) => {
        try {
            await api.post('/friends/requests', { receiver_id: userId });
            alert('挨拶を送りました。');
            setPendingIds((prev) => {
                const next = new Set(prev);
                next.add(userId);
                return next;
            });
        } catch (e: any) {
            alert(e.response?.data?.detail || 'Failed to send request');
        }
    };

    const acceptRequest = async (requestId: number) => {
        try {
            await api.post(`/friends/requests/${requestId}/accept`);
            setRequests(requests.filter(r => r.request_id !== requestId));
            await loadFriends();
            alert('Connected!');
        } catch (e: any) {
            alert('Error accepting request');
        }
    };

    const rejectRequest = async (requestId: number) => {
        try {
            await api.post(`/friends/requests/${requestId}/reject`);
            setRequests(requests.filter(r => r.request_id !== requestId));
        } catch (e: any) {
            alert('Error rejecting request');
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8 p-6 rounded-lg text-center"
                style={{
                    backgroundColor: `${theme.colors.card}E6`,
                    border: `1px solid ${theme.colors.border}`,
                    color: theme.colors.text
                }}>
                <h1 className="text-3xl font-serif mb-2 font-bold" style={{ color: theme.colors.primary }}>隣人</h1>
                <p className="opacity-80">同じマンションの居住者と繋がることができます。</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 mb-6 border-b" style={{ borderColor: theme.colors.border }}>
                <button
                    onClick={() => setActiveTab('my_neighbors')}
                    className={`pb-2 px-4 font-bold transition-colors flex items-center gap-2 ${activeTab === 'my_neighbors' ? 'border-b-2' : 'opacity-60'}`}
                    style={{
                        borderColor: activeTab === 'my_neighbors' ? theme.colors.primary : 'transparent',
                        color: activeTab === 'my_neighbors' ? theme.colors.primary : theme.colors.text
                    }}
                >
                    <Users size={18} /> 隣人リスト
                </button>
                <button
                    onClick={() => setActiveTab('greetings')}
                    className={`pb-2 px-4 font-bold transition-colors flex items-center gap-2 ${activeTab === 'greetings' ? 'border-b-2' : 'opacity-60'}`}
                    style={{
                        borderColor: activeTab === 'greetings' ? theme.colors.primary : 'transparent',
                        color: activeTab === 'greetings' ? theme.colors.primary : theme.colors.text
                    }}
                >
                    <UserPlus size={18} /> 挨拶 (申請) {requests.length > 0 && `(${requests.length})`}
                </button>
            </div>

            {/* Content */}
            <div className="min-h-[400px]">
                {loading && <div className="text-center p-8">Loading...</div>}

                {activeTab === 'my_neighbors' && !loading && (
                    <>
                        {friends.length === 0 ? (
                            <div className="text-center p-8 opacity-60">まだ誰とも繋がっていません。</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {friends.map(friend => (
                                    <div key={friend.user_id} className="p-4 rounded-lg flex items-center gap-4 shadow-sm"
                                        style={{ backgroundColor: `${theme.colors.card}F2`, border: `1px solid ${theme.colors.border}` }}>
                                        <button
                                            onClick={() => handleProfileNavigate(friend.user_id)}
                                            className="shrink-0"
                                            aria-label="隣人の投稿一覧へ"
                                        >
                                            <Avatar
                                                username={resolveDisplayName(friend.display_name, friend.room_no)}
                                                src={friend.avatar_path}
                                                size="md"
                                                theme={getTheme(friend.role_type || 'User', friend.floor_level)}
                                            />
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <button
                                                onClick={() => handleProfileNavigate(friend.user_id)}
                                                className="font-bold hover:underline text-left"
                                            >
                                                {resolveDisplayName(friend.display_name, friend.room_no)}
                                            </button>
                                            <div className="text-xs opacity-70">RoomNo. {friend.room_no || '-'}</div>
                                            <div className="text-xs opacity-70">Floor {friend.floor_level} • TOKU {friend.smile_score}</div>
                                            <div className="text-xs mt-1 px-2 py-0.5 rounded-full inline-block bg-green-100 text-green-800">隣人 (Neighbor)</div>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleProfileNavigate(friend.user_id)}
                                        >
                                            投稿一覧
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'greetings' && !loading && (
                    <div className="space-y-8">
                        {/* Pending Requests */}
                        <div>
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.colors.primary }}>
                                <UserCheck size={20} /> 届いている挨拶 (申請)
                            </h3>
                            {requests.length === 0 ? (
                                <p className="opacity-60 mb-4">届いている挨拶はありません。</p>
                            ) : (
                                <div className="grid gap-4">
                                    {requests.map(req => (
                                        <div key={req.request_id} className="p-4 rounded-lg flex justify-between items-center shadow-sm"
                                            style={{ backgroundColor: `${theme.colors.card}F2`, border: `1px solid ${theme.colors.border}` }}>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => handleProfileNavigate(req.requester_id)}
                                                    className="shrink-0"
                                                    aria-label="申請者プロフィールへ"
                                                >
                                                    <Avatar
                                                        username={resolveDisplayName(req.requester_display_name, req.requester_room_no)}
                                                        src={req.requester_avatar_path}
                                                        size="md"
                                                        theme={getTheme(req.requester_role_type || 'User', req.requester_floor_level || 1)}
                                                    />
                                                </button>
                                                <div>
                                                    <button
                                                        onClick={() => handleProfileNavigate(req.requester_id)}
                                                        className="font-bold hover:underline"
                                                    >
                                                        {resolveDisplayName(req.requester_display_name, req.requester_room_no)}
                                                    </button>
                                                    <div className="text-xs opacity-60">{new Date(req.created_at).toLocaleDateString()}</div>
                                                    <div className="text-xs opacity-70">挨拶が届いています。</div>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" onClick={() => acceptRequest(req.request_id)} className="bg-green-600 hover:bg-green-700">承認</Button>
                                                <Button size="sm" variant="outline" onClick={() => rejectRequest(req.request_id)} className="text-red-600 hover:bg-red-50">拒否</Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Search */}
                        <div className="pt-6 border-t" style={{ borderColor: theme.colors.border }}>
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.colors.primary }}>
                                <Search size={20} /> 住人を探す
                            </h3>
                            <div className="flex gap-2 mb-4">
                                <Input
                                    placeholder="RoomNo. で検索..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                    className="max-w-md"
                                />
                                <Button onClick={handleSearch}>検索</Button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {searchResults.map(user => (
                                    <div key={user.user_id} className="p-4 rounded-lg flex justify-between items-center shadow-sm"
                                        style={{ backgroundColor: `${theme.colors.card}F2`, border: `1px solid ${theme.colors.border}` }}>
                                        <div className="flex items-center gap-3">
                                            <Avatar
                                                username={resolveDisplayName(user.display_name, user.room_no)}
                                                src={user.avatar_path}
                                                size="md"
                                                theme={getTheme(user.role_type || 'User', user.floor_level)}
                                            />
                                            <div>
                                                <div className="font-bold">{resolveDisplayName(user.display_name, user.room_no)}</div>
                                                <div className="text-xs opacity-70">RoomNo. {user.room_no || '-'}</div>
                                                <div className="text-xs opacity-70">Floor {user.floor_level}</div>
                                            </div>
                                        </div>
                                        {(() => {
                                            const isFriend = friendIds.has(user.user_id);
                                            const isPending = pendingIds.has(user.user_id);
                                            const label = isFriend ? '隣人済み' : isPending ? '申請済み' : '挨拶する';
                                            return (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={isFriend || isPending}
                                                    onClick={() => sendRequest(user.user_id)}
                                                >
                                                    <UserPlus size={16} className="mr-1" /> {label}
                                                </Button>
                                            );
                                        })()}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
